import React from 'react'
import { Button, Card, CardContent, CardHeader, Select, Badge, Input, Label } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { supabase } from '@/lib/supabase'
import { getOwnerContactsByAccountIds } from '@/modules/accounts/services/ownerContactService'
import { listAllRewardsLinks, setManualCommissionConfirmed } from '../../services/rewardsAdminService'
import { getOpenRewardsCycle, listRewardsCycleMetrics } from '../../services/rewardsCyclesService'
import type { RewardsCycle, RewardsCycleExchangeMetric, RewardsExchange } from '../../types'

type AccountOption = { accountId: string; label: string; exchanges: RewardsExchange[] }

function formatUsdt(n: number) {
  const v = Number(n || 0)
  return Number.isFinite(v) ? v.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '0'
}

export default function RewardsSettingsManualCommissionsCard() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [options, setOptions] = React.useState<AccountOption[]>([])
  const [accountId, setAccountId] = React.useState<string>('')
  const [cycle, setCycle] = React.useState<RewardsCycle | null>(null)
  const [metrics, setMetrics] = React.useState<RewardsCycleExchangeMetric[]>([])

  const loadAccounts = React.useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const links = await listAllRewardsLinks()
      const linked = links.filter((l) => l.status === 'linked')
      const accountIds = Array.from(new Set(linked.map((l) => l.account_id)))
      const owners = await getOwnerContactsByAccountIds(accountIds)

      const exchangesByAcc = new Map<string, Set<RewardsExchange>>()
      for (const l of linked) {
        const set = exchangesByAcc.get(l.account_id) || new Set<RewardsExchange>()
        set.add(l.exchange)
        exchangesByAcc.set(l.account_id, set)
      }

      const opts: AccountOption[] = accountIds.map((id) => {
        const ow = (owners as any).get(id) || null
        const label = ow?.email || ow?.name || id
        const ex = Array.from(exchangesByAcc.get(id) || [])
        return { accountId: id, label, exchanges: ex }
      })

      opts.sort((a, b) => a.label.localeCompare(b.label))
      setOptions(opts)
      if (!accountId && opts.length > 0) setAccountId(opts[0].accountId)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar contas vinculadas')
    } finally {
      setLoading(false)
    }
  }, [accountId])

  React.useEffect(() => {
    loadAccounts()
  }, [loadAccounts])

  const loadCycle = React.useCallback(async () => {
    if (!accountId) return
    setError(null)
    setSaving(true)
    try {
      const c = await getOpenRewardsCycle(accountId)
      setCycle(c)
      if (c?.id) setMetrics(await listRewardsCycleMetrics(c.id))
      else setMetrics([])
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar ciclo')
    } finally {
      setSaving(false)
    }
  }, [accountId])

  React.useEffect(() => {
    if (!accountId) return
    loadCycle()
  }, [accountId, loadCycle])

  async function runSyncNow() {
    if (!accountId) return
    setSaving(true)
    try {
      await supabase.functions.invoke('rewards-sync-runner', { body: { account_id: accountId, limit: 1 } as any })
      toast({ title: 'Rewards', description: 'Sync disparado.', variant: 'success', soft: true })
      await loadCycle()
    } catch (e: any) {
      toast({ title: 'Rewards', description: e?.message || 'Falha ao disparar sync', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function saveMetric(exchange: RewardsExchange, volumeUsdt: number, commissionConfirmedUsdt: number) {
    if (!accountId) return
    setSaving(true)
    try {
      const { data } = await supabase.auth.getUser()
      const uid = (data as any)?.user?.id || null
      await setManualCommissionConfirmed({
        accountId,
        exchange,
        commissionConfirmedUsdt,
        volumeUsdt,
        updatedBy: uid,
      })
      toast({ title: 'Rewards', description: 'Comissao confirmada atualizada.', variant: 'success', soft: true })
      await loadCycle()
    } catch (e: any) {
      toast({ title: 'Rewards', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const manualMetrics = React.useMemo(() => metrics.filter((m) => m.exchange !== 'bingx'), [metrics])

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Rewards - Ciclo e ajustes manuais (MASTER)</CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-foreground/70">
          O desconto oficial do ciclo usa <span className="font-medium text-foreground">apenas comissao confirmada</span>. Para BingX, os dados sao
          sincronizados automaticamente. Este card serve para corretoras em modo v1/manual (ex.: Bybit/Bitget) e para disparar o sync do ciclo.
        </p>

        {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-foreground">{error}</div>}

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-[260px] space-y-1.5">
            <Label>Conta vinculada</Label>
            <Select value={accountId} onChange={(e) => setAccountId((e.target as HTMLSelectElement).value)} disabled={loading || saving}>
              {options.map((o) => (
                <option key={o.accountId} value={o.accountId}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={loadAccounts} disabled={loading || saving}>
              Recarregar contas
            </Button>
            <Button onClick={runSyncNow} disabled={!accountId || saving}>
              {saving ? 'Aguarde...' : 'Gerar/atualizar ciclo (sync)'}
            </Button>
          </div>
        </div>

        {!cycle ? (
          <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm text-foreground/70">
            Nenhum ciclo aberto encontrado para esta conta. Dispare o sync para criar o ciclo.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 p-3">
              <Badge variant="soft" color="info">
                Ciclo aberto
              </Badge>
              <div className="text-sm text-foreground/80">
                {new Date(cycle.period_start).toLocaleDateString('pt-BR')} - {new Date(cycle.period_end).toLocaleDateString('pt-BR')}
              </div>
              <div className="ml-auto text-sm text-foreground/70">
                Confirmada total: <span className="font-medium text-foreground">{formatUsdt(cycle.commission_confirmed_usdt_total)} USDT</span>
              </div>
            </div>

            {metrics.length === 0 ? (
              <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm text-foreground/70">Sem metricas por corretora ainda.</div>
            ) : manualMetrics.length === 0 ? (
              <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm text-foreground/70">
                Nenhuma corretora em modo manual para esta conta. (BingX sincroniza automaticamente.)
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-3">
                {manualMetrics.map((m) => (
                  <MetricEditor key={m.exchange} metric={m} disabled={saving} onSave={saveMetric} />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )

  function MetricEditor({
    metric,
    disabled,
    onSave,
  }: {
    metric: RewardsCycleExchangeMetric
    disabled: boolean
    onSave: (exchange: RewardsExchange, volumeUsdt: number, commissionConfirmedUsdt: number) => Promise<void>
  }) {
    const [volume, setVolume] = React.useState<number>(metric.volume_usdt || 0)
    const [confirmed, setConfirmed] = React.useState<number>(metric.commission_confirmed_usdt || 0)

    return (
      <div className="rounded-md border border-border/60 p-3">
        <div className="mb-2 text-sm font-semibold">{metric.exchange.toUpperCase()}</div>
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-xs">Volume (USDT)</Label>
            <Input type="number" value={volume} onChange={(e) => setVolume(Number((e.target as HTMLInputElement).value))} disabled={disabled} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Comissao confirmada (USDT)</Label>
            <Input type="number" value={confirmed} onChange={(e) => setConfirmed(Number((e.target as HTMLInputElement).value))} disabled={disabled} />
          </div>
          <Button variant="outline" onClick={() => onSave(metric.exchange, volume, confirmed)} disabled={disabled}>
            Salvar
          </Button>
          <div className="text-xs text-foreground/60">
            Estimada atual: <span className="font-medium">{formatUsdt(metric.commission_estimated_usdt)} USDT</span>
          </div>
        </div>
      </div>
    )
  }
}

